import {
  DataTable,
  DataTableColumn,
  DataTableOptions,
  DataType,
  FilterType,
  RowCustomAction
} from '@globaleris/ui.library'
import { Block, RemoveCircleOutline } from '@material-ui/icons'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { EnumEstadoUsuarioAtlas } from '../../../../../api/oData/enums/enumEstadoUsuarioAtlas'
import { EnumRolAtlas } from '../../../../../api/oData/enums/enumRolAtlas'
import { StandardCard } from '../../../../../components/shared/standardCard/standardCard'
import { getCustomerDomain } from '../../../../../redux'

export interface SuperUserTableModel {
  id: number
  email: string
  authorNameAtlas: string
  rol: EnumRolAtlas
  estado: EnumEstadoUsuarioAtlas
}

interface SuperUsersCardProps {
  data: SuperUserTableModel[]
  isLoading?: boolean
  onAdd: () => void
  onRemovePermission: (row: SuperUserTableModel) => void
  onDeactivate: (row: SuperUserTableModel) => void
}

export const SuperUsersCard: React.FC<SuperUsersCardProps> = ({
  data,
  isLoading,
  onAdd,
  onRemovePermission,
  onDeactivate
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'atlasAccess', 'enums'])

  const noData = !isLoading && data && data.length === 0

  // el DataTable no soporta un renderer custom por celda acá - se precomputan los labels traducidos
  const tableData = data.map(row => ({
    ...row,
    rolLabel: t(`enums:EnumRolAtlas.${EnumRolAtlas[row.rol]}`),
    estadoLabel: t(`enums:EnumEstadoUsuarioAtlas.${EnumEstadoUsuarioAtlas[row.estado]}`)
  }))

  // #region DataTable Config Starts
  const superUsersTableColumns: DataTableColumn[] = [
    {
      name: 'id',
      display: false
    },
    {
      name: 'email',
      label: t('atlasAccess:components.superUsersCard.columns.email'),
      type: DataType.TEXT
    },
    {
      name: 'rolLabel',
      label: t('atlasAccess:components.superUsersCard.columns.rol'),
      type: DataType.TEXT
    },
    {
      name: 'estadoLabel',
      label: t('atlasAccess:components.superUsersCard.columns.estado'),
      type: DataType.TEXT
    }
  ]

  const superUsersTableOptions: DataTableOptions = {
    i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
    search: true,
    elevation: 0,
    filterType: FilterType.ADVANCED,
    download: false,
    canEdit: false,
    canDelete: false,
    canInsert: true,
    customInsertEditModal: true,
    onRowInsert: async () => {
      onAdd()
      return true
    },
    pagination: true,
    analytics: {
      customer: getCustomerDomain(),
      view: t('atlasAccess:components.superUsersCard.analytics')
    },
    rowCustomActions: [
      {
        icon: <RemoveCircleOutline fontSize="small" />,
        tooltip: t('atlasAccess:components.superUsersCard.tooltips.removePermission'),
        onClick: onRemovePermission
      },
      {
        icon: <Block fontSize="small" />,
        tooltip: t('atlasAccess:components.superUsersCard.tooltips.deactivate'),
        onClick: onDeactivate
      }
    ] as RowCustomAction[]
  }
  // #endregion DataTable Config Ends

  return (
    <StandardCard
      variant="outlined"
      title={t('atlasAccess:components.superUsersCard.title')}
      showEmptyContent={noData}
      emptyCaption={t('atlasAccess:components.superUsersCard.emptyCaption')}
      emptyButtonCallback={onAdd}
      emptyButtonCaption={t('atlasAccess:components.superUsersCard.insertTitle')}
    >
      <DataTable
        data={isLoading ? undefined : tableData}
        columns={superUsersTableColumns}
        options={superUsersTableOptions}
      />
    </StandardCard>
  )
}
